import { isPlainObject } from 'lodash-es'

// Opaque payloads from the dialer: their keys are data, so they pass through as stored.
const OPAQUE_FIELDS = new Set(['dispatchErrors'])

const ORDERING_FIELDS = {
  batch_config__name: 'batchName',
  batch_config__merchant_location_config__merchant_location__name:
    'merchantLocationName',
  batch_config__agent__name: 'agentName',
  trigger_timestamp: 'triggerTimestamp',
  status: 'status',
  expected_call_count: 'expectedCallCount'
}

const FILTER_KEYS = {
  search: 'search',
  status: 'status',
  start_date: 'startDate',
  end_date: 'endDate'
}

function snakeCase(key) {
  return key.replace(/[A-Z]/g, letter => `_${letter.toLowerCase()}`)
}

/**
 * A BlytzCollect configuration row from ai-collector-service in the snake_case shape Django's
 * `api/crm` serializers returned, which the BlytzCollect screens and store read. Keys are converted
 * at every depth except inside `dispatchErrors`.
 *
 * @param {object|object[]} value A response body, or one row of it.
 * @returns {object|object[]} The same data with Django's key names.
 */
export function toDjangoBlytzCollectShape(value) {
  if (Array.isArray(value)) {
    return value.map(toDjangoBlytzCollectShape)
  }

  if (!isPlainObject(value)) {
    return value
  }

  return Object.fromEntries(
    Object.entries(value).map(([key, child]) => [
      snakeCase(key),
      OPAQUE_FIELDS.has(key) ? child : toDjangoBlytzCollectShape(child)
    ])
  )
}

/**
 * The batch-runs query the gateway route takes, from the page and the Django-named filters the runs
 * view builds. An ordering the route does not know is dropped, so the list falls back to newest first.
 *
 * @param {{page?: number, pageSize?: number, filters?: object}} options The view's request.
 * @returns {object} Query parameters for `aiBatches`.
 */
export function batchRunsParams({ page, pageSize, filters = {} }) {
  const params = {}
  if (page !== undefined) params.page = page
  if (pageSize !== undefined) params.pageSize = pageSize

  for (const [djangoKey, key] of Object.entries(FILTER_KEYS)) {
    if (filters[djangoKey] !== undefined) params[key] = filters[djangoKey]
  }

  if (typeof filters.ordering === 'string') {
    const descending = filters.ordering.startsWith('-')
    const field =
      ORDERING_FIELDS[filters.ordering.replace(/^-/, '')] ?? undefined
    if (field) params.ordering = `${descending ? '-' : ''}${field}`
  }

  return params
}

const camelCase = key =>
  key.replace(/_([a-z])/g, (_match, letter) => letter.toUpperCase())

/**
 * A form's Django-named fields as the gateway route takes them: keys camelCased at every depth, so a
 * location's agents are converted too; values as they are.
 *
 * @param {object|object[]} payload The body a BlytzCollect screen builds.
 * @returns {object|object[]} The same body with camelCase keys.
 */
export function toGatewayShape(payload) {
  if (Array.isArray(payload)) {
    return payload.map(toGatewayShape)
  }
  if (!isPlainObject(payload)) {
    return payload
  }

  return Object.fromEntries(
    Object.entries(payload).map(([key, value]) => [
      camelCase(key),
      toGatewayShape(value)
    ])
  )
}

/**
 * A gateway refusal with its message where the BlytzCollect views read Django's: `cfAxios` has no
 * interceptor to put it on `error.normalized`, so the views would otherwise show axios's status text.
 *
 * @param {Error} error A `cfAxios` rejection.
 * @returns {Error} The same error, carrying the gateway's message when it sent one.
 */
export function gatewayError(error) {
  const message = error?.response?.data?.message
  if (typeof message !== 'string' || message === '') {
    return error
  }
  error.message = message
  error.normalized = { message, fieldErrors: {} }

  return error
}
